<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';

import ActiveFilterPreview from 'dashboard/components-next/filter/ActiveFilterPreview.vue';

const props = defineProps({
  activeSegment: { type: Object, default: null },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const { t } = useI18n();
const route = useRoute();

const appliedFilters = useMapGetter('contacts/getAppliedContactFiltersV4');
const activeSegmentId = computed(() => route.params.segmentId);

const activeSegmentQuery = computed(() => {
  const segQuery = props.activeSegment?.query;
  if (!segQuery) return [];

  // New grouped segment format: { groups: [...] }
  if (segQuery.groups && Array.isArray(segQuery.groups)) {
    return segQuery.groups.flatMap(group =>
      (group.conditions || []).map(c => {
        const transformed = useCamelCase(c);
        return {
          attributeKey: transformed.attributeKey || c.attribute_key,
          filterOperator: transformed.filterOperator || c.filter_operator,
          queryOperator: transformed.queryOperator || c.query_operator,
          values: transformed.values ?? c.values,
        };
      })
    );
  }

  // Legacy flat format: { payload: [...] }
  const query = segQuery.payload;
  if (!Array.isArray(query)) return [];

  return query.map(filter => {
    const transformed = useCamelCase(filter);
    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values: transformed.values,
    };
  });
});

const hasActiveSegments = computed(
  () => props.activeSegment && activeSegmentId.value !== 0
);

const activeFilterQueryData = computed(() => {
  if (hasActiveSegments.value) return activeSegmentQuery.value;

  const filters = appliedFilters.value;
  if (!filters) return [];

  // New grouped format: { groups: [{ conditions: [...], group_operator }] }
  if (filters.groups && Array.isArray(filters.groups)) {
    return filters.groups.flatMap(group =>
      (group.conditions || []).map(c => {
        const transformed = useCamelCase(c);
        return {
          attributeKey: transformed.attributeKey || c.attribute_key,
          filterOperator: transformed.filterOperator || c.filter_operator,
          queryOperator: transformed.queryOperator || c.query_operator,
          values: transformed.values ?? c.values,
        };
      })
    );
  }

  // Legacy flat array format
  return Array.isArray(filters) ? filters : [];
});
</script>

<template>
  <ActiveFilterPreview
    :applied-filters="activeFilterQueryData"
    :max-visible-filters="2"
    :more-filters-label="
      t('CONTACTS_LAYOUT.FILTER.ACTIVE_FILTERS.MORE_FILTERS', {
        count: activeFilterQueryData.length - 2,
      })
    "
    :clear-button-label="
      t('CONTACTS_LAYOUT.FILTER.ACTIVE_FILTERS.CLEAR_FILTERS')
    "
    :show-clear-button="!hasActiveSegments"
    class="max-w-[60rem] px-6"
    @open-filter="emit('openFilter')"
    @clear-filters="emit('clearFilters')"
  />
</template>
